import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Plus, Trash2, X } from 'lucide-react';
import type { OwnershipOverview } from '../../../../api/pets';
import { updatePet, removePetOwner } from '../../../../api/pets';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { AddOwnerCaretakerModal } from './AddOwnerCaretakerModal';

type CurrentUserRole = 'PRIMARY_OWNER' | 'CO_OWNER' | 'CARETAKER';

interface OwnershipOverviewCardProps {
  petId: string;
  petName: string;
  ownershipOverview: OwnershipOverview;
  currentUserRole: CurrentUserRole;
  onRefresh: () => void;
}

function getInitials(
  email: string,
  fullName?: string,
  primaryOwnerLabel?: string
): string {
  if (fullName && primaryOwnerLabel && fullName !== primaryOwnerLabel) {
    const parts = fullName.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return fullName.slice(0, 2).toUpperCase();
  }
  const local = email.split('@')[0];
  return local.slice(0, 2).toUpperCase();
}

export function OwnershipOverviewCard({
  petId,
  petName,
  ownershipOverview,
  currentUserRole,
  onRefresh,
}: OwnershipOverviewCardProps) {
  const { t } = useTranslation();
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<{
    email: string;
    role: string;
  } | null>(null);
  const [removing, setRemoving] = useState(false);

  const canAddRemove =
    currentUserRole === 'PRIMARY_OWNER' || currentUserRole === 'CO_OWNER';

  const handleAdd = async (email: string, role: 'CO_OWNER' | 'CARETAKER') => {
    try {
      const existing = ownershipOverview.secondaryOwners.map((s) => s.email);
      const newEmails = existing.includes(email)
        ? existing
        : [...existing, email];
      const newRoles: Record<string, 'CO_OWNER' | 'CARETAKER'> = {};
      ownershipOverview.secondaryOwners.forEach((s) => {
        newRoles[s.email] = s.role;
      });
      newRoles[email] = role;

      const res = await updatePet(petId, {
        secondaryOwnerEmails: newEmails,
        secondaryOwnerRoles: newRoles,
      });
      if (
        res.invitationEmailFailures?.length &&
        res.invitationEmailFailures.includes(email)
      ) {
        const reason = res.invitationEmailError
          ? ` ${res.invitationEmailError}`
          : '';
        const roleLabel =
          role === 'CO_OWNER'
            ? t('petOverview.coParent')
            : t('petOverview.caretaker');
        toast.warning(
          t('petOverview.addedAsRoleInvitationFail', {
            email,
            role: roleLabel,
            reason,
          })
        );
      } else {
        toast.success(t('petOverview.invitationSent', { email }));
      }
      setAddModalOpen(false);
      onRefresh();
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : t('petOverview.failedToAdd')
      );
    }
  };

  const handleRemoveConfirm = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await removePetOwner(petId, removeTarget.email);
      toast.success(t('petOverview.accessRevoked'));
      setRemoveTarget(null);
      onRefresh();
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : t('petOverview.failedToRemove')
      );
    } finally {
      setRemoving(false);
    }
  };

  const { primaryOwner, secondaryOwners } = ownershipOverview;

  return (
    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-white/8">
      <h4 className="text-sm font-medium text-gray-700 dark:text-white/80 mb-3 flex items-center gap-2">
        <Users className="w-4 h-4" />
        {t('petOverview.ownershipCare')}
      </h4>
      <div className="space-y-2">
        {/* Primary Owner */}
        <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50 dark:bg-white/5">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-full bg-[#594BFF]/20 dark:bg-[#8B7FFF]/20 flex items-center justify-center flex-shrink-0 text-sm font-medium text-[#594BFF] dark:text-[#8B7FFF]">
              {getInitials(
                primaryOwner.email,
                primaryOwner.fullName,
                t('petOverview.primaryOwner')
              )}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {primaryOwner.fullName ||
                  primaryOwner.email ||
                  t('petOverview.primaryOwner')}
              </div>
              {primaryOwner.email && (
                <div className="text-xs text-gray-500 dark:text-white/50 truncate">
                  {primaryOwner.email}
                </div>
              )}
            </div>
          </div>
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-[#594BFF]/15 text-[#594BFF] dark:bg-[#8B7FFF]/30 dark:text-[#8B7FFF] flex-shrink-0">
            {t('petOverview.primaryOwner')}
          </span>
        </div>

        {/* Secondary: Co-Parents & Caretakers */}
        {secondaryOwners.map((s) => (
          <div
            key={s.email}
            className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50 dark:bg-white/5"
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="w-9 h-9 rounded-full bg-gray-200 dark:bg-white/10 flex items-center justify-center flex-shrink-0 text-sm font-medium text-gray-600 dark:text-white/70">
                {getInitials(s.email)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-gray-900 dark:text-white truncate">
                  {s.email}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-200 dark:bg-white/10 text-gray-700 dark:text-white/70">
                    {s.role === 'CO_OWNER'
                      ? t('petOverview.coParent')
                      : t('petOverview.caretaker')}
                  </span>
                  <span
                    className={`px-2 py-0.5 rounded text-xs font-medium ${
                      s.status === 'ACTIVE'
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                        : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                    }`}
                  >
                    {s.status === 'ACTIVE'
                      ? t('petOverview.active')
                      : t('petOverview.pendingAcceptance')}
                  </span>
                </div>
              </div>
            </div>
            {canAddRemove && (
              <button
                type="button"
                onClick={() =>
                  setRemoveTarget({ email: s.email, role: s.role })
                }
                className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex-shrink-0"
                aria-label={`Remove ${s.email}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      {canAddRemove && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setAddModalOpen(true);
            }}
            className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 border-dashed border-gray-300 dark:border-white/20 text-gray-600 dark:text-white/60 hover:border-[#594BFF] hover:text-[#594BFF] dark:hover:border-[#8B7FFF] dark:hover:text-[#8B7FFF] transition-colors text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            {t('petOverview.addOwnerOrCaretaker')}
          </button>
          <AddOwnerCaretakerModal
            open={addModalOpen}
            onClose={() => setAddModalOpen(false)}
            petName={petName}
            onSubmit={handleAdd}
          />
        </>
      )}

      {removeTarget && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="remove-owner-title"
        >
          <div
            className="absolute inset-0 bg-black/50"
            aria-hidden="true"
            onClick={() => !removing && setRemoveTarget(null)}
          />
          <div
            className="relative z-10 w-full max-w-md rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-[#16151D] p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2
                id="remove-owner-title"
                className="text-lg font-semibold text-gray-900 dark:text-white"
              >
                {t('petOverview.removeCoParentCaretaker')}
              </h2>
              <button
                type="button"
                onClick={() => !removing && setRemoveTarget(null)}
                className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-white/70 dark:hover:text-white dark:hover:bg-white/10 transition-colors"
                aria-label={t('common.close')}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-white/70 mb-6">
              {t('petOverview.removeConfirmText', {
                email: removeTarget.email,
                role:
                  removeTarget.role === 'CO_OWNER'
                    ? t('petOverview.coParent')
                    : t('petOverview.caretaker'),
              })}
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setRemoveTarget(null)}
                disabled={removing}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={handleRemoveConfirm}
                disabled={removing}
              >
                {removing ? t('petOverview.removing') : t('petOverview.remove')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
